import React from 'react';
import PropTypes from 'prop-types';

import { Box, Chip, Stack, CardMedia, Typography } from '@mui/material';

import { log } from 'src/utils/logger';
import { countryLabel } from 'src/utils/platform/channel-label';
import { highlightTextWithHtmlRemoval } from 'src/utils/text/text-highlight';
import { formatNumber, formatTimestamp } from 'src/utils/format/format-utils';
import { getMetricsByScoreTime } from 'src/utils/metrics/post-metric-snapshot';
import { readNegativeEvidence } from 'src/utils/moments/view/negative-evidence';

import { RADIUS } from 'src/theme/ui-tokens';

import SafeHtml from 'src/components/cmui/post-card/SafeHtml';
import { PostCardBookmarkOverlay } from 'src/components/cmui/bookmark';
import { PostCardCampaignOverlay } from 'src/components/cmui/campaign';
import HighlightedText from 'src/components/cmui/post-card/HighlightedText';

import PostCard from './PostCard';



/**
 * 리포스트/인용/댓글 게시물 카드 컴포넌트
 * 
 * 이 컴포넌트는 다음 상황에서 사용됩니다:
 * 1. 리포스트 (isPosted=true): 리포스트한 사용자 정보를 헤더에 표시하고, 원본 게시물을 PostCard에 표시
 * 2. 인용/댓글 (originPost 존재): 인용/댓글한 사용자 정보를 헤더에 표시하고, 원본 게시물(originPost)을 PostCard에 표시
 * 
 * @param {Object} post - 게시물 데이터
 * @param {boolean} post.isPosted - 리포스트 여부 (true인 경우 리포스트로 처리)
 * @param {Object} post.originPost - 원본 게시물 데이터 (인용/댓글인 경우)
 * @param {string} post.repostAuthor - 리포스트한 사용자 이름 (리포스트인 경우)
 * @param {string} post.repostAccount - 리포스트한 사용자 계정 (리포스트인 경우)
 * @param {Function} onClick - 카드 클릭 핸들러
 * @param {string} searchKeyword - 검색 키워드
 * @param {string} clientSearch - 클라이언트 검색어
 * @param {Array} topicKeywords - 토픽 키워드 배열
 * @param {Object} expandedSelectedBrands - 확장된 선택된 브랜드
 * @param {boolean} external - 외부 사용 여부
 */
/**
 * PostCardRepost에서 사용할 데이터를 상황별로 정의하는 함수
 *
 * 리포스트: post-ingest의 normalizeUnifiedRepostPost 이후 규약
 * - 헤더: repostAuthor / repostAccount
 * - 본문 카드: originPost
 *
 * @param {Object} post - 게시물 데이터
 * @returns {Object} { headerData, cardData, platformIconPath }
 *   - headerData: 헤더에 표시할 작성자 정보 (author, account, source)
 *   - cardData: PostCard에 전달할 게시물 데이터
 *   - platformIconPath: 플랫폼 아이콘 경로
 */
const getPostCardRepostData = (post: any) => {
  // 전처리 단계에서 설정된 플래그 사용
  const isQuote = Boolean(post?.isQuote);
  const isRepost = Boolean(post?.isRepost);

  // originPost 데이터 (인용인 경우 필요)
  const originPostData = post?.originPost || post?._source?.originPost;

  // 플랫폼 정보
  const source = post?.source;
  const platformIconPath = source ? `/assets/icons/platforms/${String(source).toLowerCase()}.svg` : null;

  // 리포스트: 통일 규약 — originPost + repostAuthor/repostAccount (post-ingest)
  if (isRepost) {
    const headerData = {
      repostAuthor: post?.repostAuthor || '',
      repostAccount: post?.repostAccount || '',
      author: post?.author || '',
      account: post?.account || '',
      source: post?.source,
    };

    const hasUsableOriginPost =
      originPostData &&
      typeof originPostData === 'object' &&
      Object.keys(originPostData).length > 0;

    const cardData = hasUsableOriginPost
      ? {
        ...originPostData,
        content: originPostData.text || originPostData.content,
        title: originPostData.title || originPostData.text,
        id: originPostData.postId || originPostData.id,
      }
      : {
        ...post,
        isRepost: true,
      };

    return {
      headerData,
      cardData,
      platformIconPath,
      isRepost: true,
      isQuote: false,
    };
  }

  // 상황 2: 인용/댓글 (isQuoted = true)
  if (isQuote && originPostData) {
    const headerData = {
      // 헤더: post 데이터 자체를 다 전달
      ...post,
    };
    const cardData = {
      // PostCard: originPost 객체를 전달
      ...originPostData,
      // 필드명 매핑
      content: originPostData.text || originPostData.content,
      title: originPostData.title || originPostData.text,
      id: originPostData.postId || originPostData.id,
      isQuote: true,
    };

    return {
      headerData,
      cardData,
      platformIconPath,
      isQuote,
      isRepost: false,
    };
  }

  // 그 외의 시나리오는 없음 (이 경우는 PostCardRepost가 아닌 PostCard로 표시되어야 함)
  // 하지만 안전을 위해 기본값 반환
  const headerData = {
    author: post?.author || '',
    account: post?.account || '',
    source: post?.source,
  };
  const cardData = {
    ...post,
  };

  // 리포스트도 인용도 아닌 글이 이 부품에 들어온 것 — 부르는 쪽의 분기 실수다. 글을 통째로 남기지 않는다
  log.debug('PostCardRepost', '리포스트도 인용도 아닌 글이 들어왔다', {
    id: post?.id,
    source: post?.source,
    isQuote: post?.isQuote,
    isRepost: post?.isRepost,
  });

  return {
    headerData,
    cardData,
    platformIconPath,
    isQuote: false,
    isRepost: false,
  };
};

function PostCardRepost({
  post,
  onClick,
  searchKeyword = '',
  clientSearch = '',
  topicKeywords = [],
  expandedSelectedBrands,
  external = false,
  enableImageBookmark = false,
  enableCampaignAdd = false,
  isBookmark = false,
  selectedPostIds = new Set(),
  onToggleSelect,
}: {
  post: any
  onClick?: (post: any) => void
  searchKeyword?: string | readonly string[]
  clientSearch?: string
  topicKeywords?: readonly string[]
  /** ⚠️ `Set` 도 배열도 온다 — 코드가 `.has` 유무로 오리 타이핑한다 */
  expandedSelectedBrands?: any
  external?: boolean
  enableImageBookmark?: boolean
  enableCampaignAdd?: boolean
  isBookmark?: boolean
  selectedPostIds?: ReadonlySet<string>
  onToggleSelect?: (...args: any[]) => void
}) {
  // 상황별 데이터 가져오기
  const { headerData, cardData, platformIconPath, isQuote, isRepost } = getPostCardRepostData(post);

  // external prop이 true인 경우 (PostCardRepost 안에 있는 PostCard) 플랫폼 아이콘 숨김
  const shouldHidePlatformIcon = external;

  // isQuote일 때 헤더 데이터 준비
  const headerPost = isQuote ? headerData : null;
  const headerTimeDisplay = React.useMemo(() => {
    if (!headerPost) return '';
    if (headerPost?.timestamp) return formatTimestamp(headerPost.timestamp);
    if (headerPost?.date) return formatTimestamp(headerPost.date);
    return '';
  }, [headerPost]);

  // 헤더 메트릭 정보 계산
  const headerLatestMetrics = React.useMemo(() => {
    if (!headerPost) {
      return { likeCount: 0, commentCount: 0, viewCount: 0 };
    }
    return getMetricsByScoreTime(headerPost);
  }, [headerPost]);

  // 리포스트(겉 글) 메타: 수치/날짜/북마크는 "리포스트한 사람의 글(post)" 기준으로 표시
  const outerTimeDisplay = React.useMemo(() => {
    if (post?.timestamp) return formatTimestamp(post.timestamp)
    if (post?.date) return formatTimestamp(post.date)
    return ''
  }, [post?.timestamp, post?.date])

  const outerLatestMetrics = React.useMemo(() => getMetricsByScoreTime(post), [post])

  // market 정보 가져오기 (headerData에서)
  const market = headerData?.market || post?.market;
  const isHit = Boolean(headerData?.isHit || post?.isHit);

  // 리포스트면 나라를 안 적는다(원글의 나라라서) · 그 밖에는 공용 이름표(대문자 코드)
  const marketDisplay = React.useMemo(() => {
    if (isRepost) return null;
    return market ? countryLabel(market) : null;
  }, [market, isRepost]);


  // 텍스트 하이라이트 함수
  // ⚠️ 선존: 일부 호출부가 `highlightText(t, searchKeyword, topicKeywords)` 로 인자 셋을
  //    넘기지만 이 함수는 첫 인자만 읽는다(나머지는 클로저 값). 동작 보존을 위해 여분 인자를
  //    받기만 하고 쓰지 않는다 — 정리는 A475(후보 A560).
  const highlightText = (text: unknown, ..._ignoredExtraArgs: unknown[]) =>
    highlightTextWithHtmlRemoval(
      text as string,
      searchKeyword as string,
      topicKeywords as string[],
      clientSearch,
      post?.sentimentEvidencePositive || [],
      readNegativeEvidence(post)
    );

  /**
   * 🔑 신원(계정명·작성자명)은 **마크업 없이** 그린다 — `dangerouslySetInnerHTML` 을 쓰지 않는다.
   * 그 값에 태그가 필요한 이유가 없는데 HTML 통로를 열어 두면 «글쓴이가 고른 문자열»이
   * 태그가 된다. React 가 텍스트를 늘 escape 하므로 여기서는 태그가 생길 수 없다.
   */
  const renderIdentity = (text: string | null | undefined, prefix = '') => (
    <HighlightedText
      text={text}
      prefix={prefix}
      searchKeyword={searchKeyword as string}
      topicKeywords={topicKeywords as string[]}
      clientSearch={clientSearch}
      sentimentPositive={post?.sentimentEvidencePositive || []}
      sentimentNegative={readNegativeEvidence(post)}
    />
  );

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 1440,
        mx: 'auto',
      }}
    >
      {/* 리포스트 헤더 영역 */}
      <Box
        onClick={() => onClick && onClick(post)}
        sx={{
          borderRadius: RADIUS.lg,
          bgcolor: 'white',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 'none',
          overflow: 'visible', // 리포스트 아이콘이 카드 밖으로 나올 수 있도록
          // PostCard와 동일한 카드 내부 패딩
          p: { xs: 2, sm: 2.5 },
          width: '100%',
          maxWidth: 1440,
          mx: 'auto',
          height: 'fit-content',  // 추가: 내용에 맞춰 높이 조정
          display: 'flex',        // 추가: flex 컨테이너로 변경
          flexDirection: 'column', // 추가: 세로 방향
          position: 'relative', // 리포스트 아이콘 위치 지정을 위해
          cursor: 'pointer',
          '&:hover, &:active, &.Mui-focusVisible': {
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 'none',
            transform: 'none',
          },
        }}
      >
        {/* 댓글/인용/리포스트 표시: 칩으로 통일 (플래그 제거) */}
        {(isRepost || isQuote || Boolean(post?.isStory)) && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
            {isQuote && (
              <Chip
                label="인용"
                size="small"
                sx={{ height: 20, typography: 'uiAction', bgcolor: 'warning.lighter', color: 'warning.dark' }}
              />
            )}
            {isRepost && (
              <Chip
                label="리포스트"
                size="small"
                sx={{ height: 20, typography: 'uiAction', bgcolor: 'info.lighter', color: 'info.dark' }}
              />
            )}
            {Boolean(post?.isStory) && (
              <Chip
                label="스토리"
                size="small"
                sx={{ height: 20, typography: 'uiAction', bgcolor: 'error.lighter', color: 'error.dark' }}
              />
            )}
          </Stack>
        )}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ width: '100%', maxWidth: '100%', flexWrap: 'nowrap', overflow: 'hidden', minWidth: 0 }}
        >
          {/* 왼쪽: 아이콘 + 작성자 (한 줄) */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1, overflow: 'hidden' }}>
            {platformIconPath && !shouldHidePlatformIcon && (
              <Box
                component="img"
                src={platformIconPath}
                alt={headerData.source || headerData?.source}
                sx={{ width: 18, height: 18, flexShrink: 0 }}
              />
            )}
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0, overflow: 'hidden', flex: 1 }}>
              <Typography
                component="span"
                fontWeight={600}
                sx={{
                  fontSize: '15px',
                  color: 'black',
                  textAlign: 'left',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  minWidth: 0,
                  lineHeight: 1.2,
                }}
                title={headerData.repostAuthor || headerData.author || ''}
              >
                {renderIdentity(headerData.repostAuthor || headerData.author || '')}
              </Typography>
              {(headerData.repostAccount || headerData.account) && (
                <Typography
                  component="span"
                  sx={{
                    fontSize: '13.5px',
                    color: 'text.secondary',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.2,
                    flexShrink: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: { xs: '50%', sm: '40%' },
                  }}
                  title={`@${headerData.repostAccount || headerData.account}`}
                >
                  {renderIdentity(headerData.repostAccount || headerData.account, '@')}
                </Typography>
              )}

            </Box>
          </Box>

          {/* 오른쪽: Hit 배지 / 국가 표시 */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
            {isHit && (
              <Box sx={{
                px: 1,
                py: 0.25,
                borderRadius: RADIUS.sm,
                bgcolor: 'error.lighter',
                color: 'error.dark',
                fontSize: '12px',
                fontWeight: 600,
              }}>
                HIT
              </Box>
            )}
            {marketDisplay && (
              <Typography color="grey.600" variant="caption" sx={{ fontSize: '15px', fontWeight: 'light' }}>
                {marketDisplay}
              </Typography>
            )}
          </Stack>
        </Stack>


        {/* isQuote일 때: title, content, 메트릭 정보 표시 */}
        {isQuote && headerPost && (() => {
          const headerTitle = headerPost?.title || '';
          const headerContent = headerPost?.content || '';
          const headerImageSrc = headerPost?.imageS3Url || headerPost?.imageUrl || null;
          const hasHeaderImage = !!headerImageSrc;
          const hasHeaderTitle = !!headerTitle;
          const hasHeaderContent = !!headerContent;

          return (
            <Box sx={{ pt: 1.5 }}>
              {/* 이미지 */}
              {hasHeaderImage && (
                <Box mb={hasHeaderTitle || hasHeaderContent ? 2 : 0}>
                  <CardMedia
                    component="img"
                    image={headerImageSrc}
                    alt="post media"
                    onError={(e) => ((e.target as HTMLElement).style.display = "none")}
                    sx={{
                      borderRadius: RADIUS.lg,
                      aspectRatio: '1 / 1',
                      objectFit: 'cover',
                      width: '100%',
                      maxHeight: 'none',
                    }}
                  />
                </Box>
              )}

              {/* 제목 */}
              {hasHeaderTitle && (
                <Typography
                  sx={{
                    color: 'black',
                    mb: hasHeaderContent ? 0 : { xs: 1, sm: 2 },
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontSize: { xs: '13.5px', sm: '15px' },
                    textAlign: 'left',
                  }}
                >
                  <SafeHtml html={highlightText(headerTitle, searchKeyword, topicKeywords)} />
                </Typography>
              )}

              {/* 내용 */}
              {hasHeaderContent && (
                <Typography
                  variant="body2"
                  sx={{
                    mb: 0,
                    color: 'text.secondary',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    lineHeight: 1.5,
                    textAlign: 'left',
                  }}
                >
                  <SafeHtml html={highlightText(headerContent, searchKeyword, topicKeywords)} />
                </Typography>
              )}

            </Box>
          );
        })()}


        <Box
          sx={{
            width: '100%',
            mt: 0, // 간격 축소
            position: 'relative',
            '& > .MuiCard-root': {
              width: '100%',
              maxWidth: '100%',
              mx: 0,
              bgcolor: 'grey.200',
            },
          }}
        >

          <PostCard
            post={cardData}
            onClick={onClick}
            searchKeyword={searchKeyword}
            clientSearch={clientSearch}
            topicKeywords={topicKeywords}
            expandedSelectedBrands={expandedSelectedBrands}
            external
            hidePlatformIcon={false}
            showKindChips={false}
            showBookmarkButton
            enableImageBookmark={enableImageBookmark}
            enableCampaignAdd={false}
            bookmarkTargetPost={post}
            isBookmark={isBookmark}
            selectedPostIds={selectedPostIds}
            onToggleSelect={onToggleSelect}
          />
        </Box>

        {/* 메트릭/날짜/북마크 - 겉 글 기준 (리포스트/인용 모두 표시) */}
        {(isQuote || isRepost) && (
          <Stack
            direction="row"
            spacing={1}
            mt={{ xs: 1, sm: 2 }}
            mb={0}
            alignItems="center"
            justifyContent="flex-start"
            flexWrap="wrap"
          >
            {/* 좋아요 */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                height: 26,
                bgcolor: 'grey.100',
                borderRadius: RADIUS.pill,
                px: 1.5,
                py: 0.625,
              }}
            >
              <Box
                component="img"
                src="/assets/icons/icon_svg/like.svg"
                sx={{
                  width: 16,
                  height: 16,
                  filter: 'brightness(0) saturate(100%)',
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: 'black',
                  fontWeight: 'Medium',
                  fontSize: '13.5px',
                }}
              >
                {formatNumber((isQuote ? headerLatestMetrics : outerLatestMetrics).likeCount)}
              </Typography>
            </Box>

            {/* 댓글 */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                height: 26,
                bgcolor: 'grey.100',
                borderRadius: RADIUS.pill,
                px: 1.5,
                py: 0.625,
              }}
            >
              <Box
                component="img"
                src="/assets/icons/icon_svg/reply.svg"
                sx={{
                  width: 16,
                  height: 16,
                  filter: 'brightness(0) saturate(100%)',
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: 'black',
                  fontWeight: 'Medium',
                  fontSize: '13.5px',
                }}
              >
                {formatNumber((isQuote ? headerLatestMetrics : outerLatestMetrics).commentCount)}
              </Typography>
            </Box>

            {/* 조회수 */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                height: 26,
                bgcolor: 'grey.100',
                borderRadius: RADIUS.pill,
                px: 1.5,
                py: 0.625,
              }}
            >
              <Box
                component="img"
                src="/assets/icons/icon_svg/view.svg"
                sx={{
                  width: 16,
                  height: 16,
                  filter: 'brightness(0) saturate(100%)',
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: 'black',
                  fontWeight: 'Medium',
                  fontSize: '13.5px',
                }}
              >
                {post?.contentsType === 'instagram_post' && (isQuote ? headerLatestMetrics : outerLatestMetrics).viewCount === 0
                  ? '-'
                  : formatNumber((isQuote ? headerLatestMetrics : outerLatestMetrics).viewCount)}
              </Typography>
            </Box>

            <Stack direction='row' width='100%' justifyContent='space-between' alignItems='center' spacing={1}>
              {/* 시간 정보 */}
              {(isQuote ? headerTimeDisplay : outerTimeDisplay) && (
                <Typography
                  color="text.secondary"
                  variant="caption"
                  sx={{
                    fontWeight: 'Light',
                    fontSize: '13.5px',
                    color: 'text.secondary',
                  }}
                >
                  {isQuote ? headerTimeDisplay : outerTimeDisplay}
                </Typography>
              )}

              {!isBookmark && (
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  onClick={(e) => e.stopPropagation()}
                  sx={{ pointerEvents: 'auto', flexShrink: 0 }}
                >
                  <PostCardBookmarkOverlay
                    placement="postCard"
                    post={post}
                    disabled={isRepost}
                    disabledTooltip={
                      isRepost ? '리포스트 포스팅은 북마크가 불가합니다.' : ''
                    }
                  />
                  {enableCampaignAdd && (
                    <PostCardCampaignOverlay
                      placement="postCard"
                      post={post}
                    />
                  )}
                </Stack>
              )}
            </Stack>
          </Stack>
        )}

      </Box>


    </Box>
  );
}

PostCardRepost.propTypes = {
  post: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    content: PropTypes.string,
    author: PropTypes.string,
    account: PropTypes.string,
    repostAuthor: PropTypes.string,
    repostAccount: PropTypes.string,
    source: PropTypes.string,
    imageUrl: PropTypes.string,
    imageS3Url: PropTypes.string,
    timestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    date: PropTypes.string,
    market: PropTypes.string,
    brands: PropTypes.arrayOf(PropTypes.string),
    canonicalBrands: PropTypes.arrayOf(PropTypes.string),
    isPosted: PropTypes.bool,
    sentimentEvidencePositive: PropTypes.arrayOf(PropTypes.string),
    sentimentEvidenceNegative: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  external: PropTypes.bool,
  onClick: PropTypes.func,
  searchKeyword: PropTypes.string,
  clientSearch: PropTypes.string,
  topicKeywords: PropTypes.arrayOf(PropTypes.string),
  expandedSelectedBrands: PropTypes.objectOf(PropTypes.bool),
  enableImageBookmark: PropTypes.bool,
  enableCampaignAdd: PropTypes.bool,
};

export default React.memo(PostCardRepost); 